import { useDebounce } from "./debounce";
import { useEffect, useState } from "react";

export function useAutoSave<T>(
  defaultValue: T,
  saver: (v: T) => Promise<any>
): [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  boolean,
  boolean,
  string | null
] {
  const [value, setValue] = useState<T>(defaultValue);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requireSaving, setRequireSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const debouncedValue = useDebounce(value, 200);

  const save = () => {
    setSaved(false);
    setSaving(true);
    saver(debouncedValue)
      .then(() => {
        setRequireSaving(false);
        setSaved(true);
      })
      .catch((err) => {
        setErrorMsg(err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    setErrorMsg(null);
    if (!saving && debouncedValue != defaultValue) {
      setRequireSaving(true);
      return;
    }
    if (debouncedValue != defaultValue) {
      save();
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (requireSaving && !saving && !errorMsg) {
      save();
    }
  }, [saving, requireSaving, errorMsg]);

  return [value, setValue, saved, saving, errorMsg];
}
